import { ReactNode, useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";

type ProtectedRouteProps = {
  children: ReactNode;
  roles?: string[];
};

const ProtectedRoute = ({ children, roles }: ProtectedRouteProps) => {
  const { user, loading, role, checkAuth } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Use a ref to track if we've already checked auth during this component's lifecycle
  const authChecked = useRef(false);

  useEffect(() => {
    // Skip authentication check if we already have a user or if we've already checked
    if (user || authChecked.current) {
      setIsAuthenticated(!!user);
      setIsVerifying(false);

      return;
    }

    const verifyAuth = async () => {
      // Only verify if we haven't already checked during this component's lifecycle
      if (!authChecked.current) {
        setIsVerifying(true);
        // Use localStorage directly to prevent API calls where possible
        const token = localStorage.getItem("token");
        const userId = localStorage.getItem("userId");

        if (token && userId) {
          setIsAuthenticated(true);
          authChecked.current = true;
          setIsVerifying(false);
        } else {
          // Only make an API call if absolutely necessary
          const isValid = await checkAuth();

          setIsAuthenticated(isValid);
          authChecked.current = true;
          setIsVerifying(false);
        }
      }
    };

    verifyAuth();

    // Clean up
    return () => {
      // Reset the auth checked flag when component unmounts
      authChecked.current = false;
    };
  }, [checkAuth, user]);

  // Show loading state while initial loading or while verifying
  if (loading || isVerifying) {
    return <div>Loading...</div>;
  }

  // Redirect to login if not authenticated
  if (!user || !isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Check role-based access
  if (roles && role && !roles.includes(role)) {
    return <Navigate to="/unauthorized" />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
